import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ActivatedRoute,
    NavigationEnd,
    Params,
    Router,
    RouterEvent,
} from '@angular/router';
import { Observable, of, Subscription, timer } from 'rxjs';
import { filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import { PlayerEvent, PlayerVimeoComponent } from './player.vimeo.component';
import { TranslationKeyMap } from './vimeo.i18n';
import { applyLastWatched, Playlist, PlaylistItem } from './vimeo.model';
import { VimeoService } from './vimeo.service';

@Component({
    selector: 'cmps-vimeo',
    templateUrl: './vimeo.component.html',
})
export class VimeoComponent implements OnInit, OnDestroy {
    @ViewChild('player') private readonly _player: PlayerVimeoComponent;

    i18n = TranslationKeyMap;
    private readonly _routerSub: Subscription;
    private _playlistItemId: number;
    hasPendingData: boolean;
    playlists: Playlist[];
    currentPlaylist: Playlist;
    currentPlaylistItem: PlaylistItem;
    nextPlaylistItem: PlaylistItem;
    autoPlay: boolean;

    constructor(
        private readonly _router: Router,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _service: VimeoService,
    ) {
        this._routerSub = (
            this._router.events as Observable<
                RouterEvent | ActivatedRoute | Params | PlaylistItem
            >
        )
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                switchMap(() => {
                    let children = this._router.routerState.root.children;
                    while (children.length !== 0) {
                        if (children[0].children.length === 0) break;
                        children = children[0].children;
                    }
                    return of(!children ? null : children[0]);
                }),
                filter((ar) => !!ar),
                switchMap((ar) => ar.params.pipe(take(1))),
                // eslint-disable-next-line no-return-assign
                tap(
                    (data: any) =>
                        (this._playlistItemId = parseInt(data.playlistItemId)),
                ),
                filter(() => !!this.playlists),
                map(() => this._determineNext()),
                filter((next) => !!next),
                switchMap((next) =>
                    this._service
                        .listAttachments(next.playlistItemId)
                        // eslint-disable-next-line no-return-assign
                        .pipe(
                            tap((data) => (next.attachments = data)),
                            map(() => next),
                        ),
                ),
                switchMap((next: PlaylistItem) => {
                    this._changeDetectorRef.detectChanges();
                    return this._playNext(next);
                }),
            )
            .subscribe(null);
    }

    ngOnInit(): void {
        this.hasPendingData = true;
        this._service
            .listPlaylists()
            .pipe(
                tap((data) => {
                    this.playlists = data;
                    this._applyLastWatched();
                }),
                map(() => this._determineNext()),
                filter((next) => !!next),
                switchMap((next) =>
                    this._service
                        .listAttachments(next.playlistItemId)
                        // eslint-disable-next-line no-return-assign
                        .pipe(
                            tap((data) => (next.attachments = data)),
                            map(() => next),
                        ),
                ),
                switchMap((next) => {
                    this.hasPendingData = false;
                    this._changeDetectorRef.detectChanges();
                    return this._playNext(next);
                }),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.hasPendingData = false)),
            )
            .subscribe(null);
    }

    ngOnDestroy(): void {
        if (this._routerSub) {
            this._routerSub.unsubscribe();
        }
    }

    private _determineNext(): PlaylistItem {
        let playlist =
            !this.playlists || this.playlists.length === 0
                ? null
                : this.playlists[0];
        let playlistItem =
            !playlist || playlist.items.length === 0 ? null : playlist.items[0];

        if (this._playlistItemId) {
            playlistItem =
                this.playlists
                    .reduce((p, c) => {
                        p = p.concat(c.items);
                        return p;
                    }, [])
                    .find(
                        (pli) => pli.playlistItemId === this._playlistItemId,
                    ) || playlistItem;
            playlist = this.playlists.find(
                (pl) =>
                    pl.playlistId ===
                    (!playlistItem ? null : playlistItem.playlistId),
            );
        }

        this.currentPlaylist = playlist;
        this.nextPlaylistItem = playlistItem;

        return this.nextPlaylistItem;
    }

    private _applyLastWatched(): void {
        this._service
            .listViewedPlaylistItems()
            .pipe(
                filter(() => !!this.playlists),
                tap((data) => applyLastWatched(data, this.playlists)),
            )
            .subscribe(null);
    }

    private _playNext(next: PlaylistItem): Observable<number | {}> {
        if (!next) {
            console.log('end of playlist');
            return of(null);
        }
        this.currentPlaylistItem = next;
        const index =
            this.currentPlaylist.items.findIndex(
                (item) => item === this.currentPlaylistItem,
            ) + 1;
        this.nextPlaylistItem =
            index === 0 || index >= this.currentPlaylist.items.length
                ? null
                : this.currentPlaylist.items[index];
        return this._player.loadVideo(this.currentPlaylistItem);
    }

    onPlayerEvent($event: PlayerEvent): void {
        if ($event.event === 'ended') {
            this._playNext(this.nextPlaylistItem).subscribe((id) => {
                if (!id || !this.autoPlay) return;
                timer(100).subscribe(() => this._player.play().subscribe(null));
            });
        }
    }
}
